import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Drawer,
  FormControl,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  MenuItem,
  Select,
} from "@mui/material";
import { CancelOutlined, MoreHoriz, Add, Remove } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../Redux/Store";
import { clearShowMenu } from "../../../../Redux/Reducers/GlobalVar";
import CommonStatus from "../../../Common/CommonStatus/CommonStatus";
import DeletePopup from "../../../Common/DeletePopup/DeletePopup";
import { toast } from "react-toastify";
import CommonMenu from "../../../Common/CommonMenu/CommonMenu";
import CommonShimmer from "../../../Common/CommonShimmer/CommonShimmer";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import { DragIndicator } from "@mui/icons-material"; // drag icon
import {
  apiCallQuickSurveyCategoryList,
  apiCallQuickSurveyQuestionsAdd,
  apiCallQuickSurveyQuestionsList,
  apiCallQuickSurveyQuestionsUpdate,
  apiCallStatusUpdate,
} from "../../../../Core/Apicall";

interface Type {
  _id?: string;
  question: string;
  type?:"text-input" |  "radio" ;
  options?: string[];
  isActive: boolean;
  isDelete: boolean;
  displayOrder: number;
  categoryId?: string;
}

function QuickSurveyQuestions() {
  const [data, setData] = useState<Type[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [categories, setCategories] = useState<any[]>([]);
  const dispatch = useDispatch();
  const [search, setSearch] = useState("");
  const [statusDialogOpen, setStatusDialogOpen] = useState<any>(false);
  const showMenu: any = useSelector(
    (state: RootState) => state.global.showMenu
  );
  const userdata: any = useSelector((state: RootState) => state.global.token);
  const [isCreateNew, setIsCreateNew] = useState<boolean>(!!showMenu);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuVibe, setMenuVibe] = useState<Type | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [formData, setFormData] = useState<any>({
    Id: "",
    question: "",
    type: "text-input",
    options: [] as string[],
    isActive: true,
    displayOrder: "",
    isDelete: false,
    categoryId: "",
  });

  useEffect(() => {
    setIsCreateNew(!!showMenu);
  }, [showMenu]);

  useEffect(() => {
    fetchData();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await apiCallQuickSurveyCategoryList({});
      setCategories(res?.data);
    } catch (err) {
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await apiCallQuickSurveyQuestionsList({});
      const safeData: Type[] = (res?.data || []).map((d: any, idx: number) => ({
        _id: d._id,
        categoryId: d.categoryId,
        question: d.question || "",
        type: d.type || "text-input",
        options: Array.isArray(d.options) ? d.options : [],
        isActive: typeof d.isActive === "boolean" ? d.isActive : true,
        isDelete: typeof d.isDelete === "boolean" ? d.isDelete : false,
        displayOrder:
          typeof d.displayOrder === "number" ? d.displayOrder : idx + 1,
      }));

      setData(safeData);
    } catch (err) {}
    setLoading(false);
  };

  const handleChange = (e: any) => {
    const name = e.target?.name;
    const value = e.target?.value;
    if (!name) return;

    setFormData((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleOptionChange = (index: number, value: string) => {
    setFormData((prev: any) => {
      const opts = [...prev.options];
      opts[index] = value;
      return { ...prev, options: opts };
    });
  };

  const addOption = () => {
    setFormData((prev: any) => ({
      ...prev,
      options: [...prev.options, ""],
    }));
  };

  const removeOption = (index: number) => {
    setFormData((prev: any) => {
      const opts = [...prev.options];
      opts.splice(index, 1);
      return { ...prev, options: opts };
    });
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    if (!formData.question.trim()) {
      toast.error("Please enter question");
      return;
    }

    if (formData.type === "radio") {
      if (
        !formData.options.length ||
        formData.options.some((o: string) => !o.trim())
      ) {
        toast.error("Please add valid radio options");
        return;
      }
    }

    try {
      if (formData.Id) {
        const payload: any = {
          adminId: userdata?.data?.userId,
          categoryId: formData.categoryId,
          question: formData.question,
          questionId:formData.Id,
          displayOrder: formData.displayOrder,
          type: formData.type,
          options: formData.type === "radio" ? formData.options : [],
        };

        const res = await apiCallQuickSurveyQuestionsUpdate(payload);
        toast.success(res.message);
      } else {
        const payload: any = {
          adminId: userdata?.data?.userId,
          question: formData.question,
          categoryId: formData.categoryId,
          type: formData.type,
          options: formData.type === "radio" ? formData.options : [],
        };

        const res = await apiCallQuickSurveyQuestionsAdd(payload);
        toast.success(res.message);
      }

      fetchData();
      setIsCreateNew(false);

      setFormData({
        Id: "",
        question: "",
        categoryId: "",
        type: "text-input",
        options: [],
        displayOrder: "",
        isActive: true,
        isDelete: false,
      });
    } catch (err: any) {
      toast.error(err?.message || "Failed");
    }
  };

  const handleStatusUpdate = async (typeItem: any) => {
    try {
      await apiCallStatusUpdate({
        type: "quick_survey_que",
        typeId: typeItem._id,
        isActive: !typeItem.isActive,
      });

      setData((prev) =>
        prev.map((d) =>
          d._id === typeItem._id ? { ...d, isActive: !typeItem.isActive } : d
        )
      );
    } catch (err) {}
    setStatusDialogOpen(false);
  };

  const handleDelete = async () => {
    if (!menuVibe) return;
    try {
      await apiCallStatusUpdate({
        type: "quick_survey_que",
        typeId: menuVibe._id,
        isDelete: !menuVibe.isDelete,
      });

      setData((prev) => prev.filter((item) => item._id !== menuVibe._id));
    } catch (err) {}
    setDeleteDialogOpen(false);
    setMenuVibe(null);
  };

  const handleMenuOpen = (event: any, type: any) => {
    setAnchorEl(event.currentTarget);
    setMenuVibe(type);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const menuItems = [
    {
      label: "Edit",
      onClick: () => {
        if (menuVibe) {
          setFormData({
            Id: menuVibe._id,
            question: menuVibe.question,
            type: menuVibe.type || "text-input",
            options: menuVibe.options || [],
            displayOrder: menuVibe.displayOrder,
            isActive: menuVibe.isActive,
            isDelete: menuVibe.isDelete,
            categoryId: menuVibe.categoryId || "",
          });
          setIsCreateNew(true);
        }
        handleMenuClose();
      },
    },
    {
      label: "Delete",
      onClick: () => setDeleteDialogOpen(true),
    },
  ];

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    const newData = Array.from(data);
    const [movedItem] = newData.splice(result.source.index, 1);
    newData.splice(result.destination.index, 0, movedItem);

    setData(newData);

    try {
      for (let i = 0; i < newData.length; i++) {
        await apiCallQuickSurveyQuestionsUpdate({
          questionId: newData[i]._id,
          displayOrder: i + 1,
        });
      }
    } catch (err) {}

    fetchData();
  };

  return (
    <Box style={{ paddingBottom: "20px" }}>
      <Box className="d-flex input align-items-center justify-content-between">
        <TextField
          className="border-10"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ backgroundColor: "white" }}
          placeholder="Search.."
        />

        <Button
          className="btn-black p-2 px-3"
          onClick={() => {
            setFormData({
              Id: "",
              question: "",
              type: "text-input",
              options: [],
              displayOrder: "",
              isActive: true,
              isDelete: false,
            });
            setIsCreateNew(true);
          }}
        >
          + Create New
        </Button>
      </Box>

      <Paper className="bg-white mt-3 border-20 scroll-bar">
        <TableContainer className="scroll-bar">
          <DragDropContext onDragEnd={handleDragEnd}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Order</TableCell>
                  <TableCell>Question</TableCell>
                  <TableCell>Type / Options</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>

              <Droppable droppableId="kids-table">
                {(provided) => (
                  <TableBody
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {data
                      .filter(
                        (item: any) =>
                          search === "" ||
                          item.question
                            .toLowerCase()
                            .includes(search.toLowerCase())
                      )
                      .map((item: any, index: any) => (
                        <Draggable
                          key={item._id}
                          draggableId={String(item._id)}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <TableRow
                              hover
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              style={provided.draggableProps.style}
                            >
                              <TableCell>{item.displayOrder}</TableCell>

                              <TableCell>{item.question}</TableCell>

                              <TableCell>
                                <strong>
                                  {item.type === "radio"
                                    ? "Radio"
                                 :"Text Input"
                                   }
                                </strong>

                                {item.type === "radio" &&
                                  item.options &&
                                  item.options.length > 0 && (
                                    <ul style={{ margin: 0, paddingLeft: 14 }}>
                                      {item.options.map(
                                        (opt: string, i: number) => (
                                          <li key={i} style={{ fontSize: 12 }}>
                                            {opt}
                                          </li>
                                        )
                                      )}
                                    </ul>
                                  )}
                              </TableCell>

                              <TableCell>
                                <Button
                                  className={
                                    item.isActive ? "btn-success" : "btn-danger"
                                  }
                                  onClick={() => setStatusDialogOpen(index)}
                                >
                                  {item.isActive ? "Active" : "Inactive"}
                                </Button>

                                <CommonStatus
                                  open={statusDialogOpen === index}
                                  title={item?.question}
                                  message={`Are you sure you want to ${
                                    item?.isActive ? "deactivate" : "activate"
                                  } this question?`}
                                  confirmText={
                                    item?.isActive ? "Deactivate" : "Activate"
                                  }
                                  confirmColor={
                                    item?.isActive ? "warning" : "success"
                                  }
                                  onConfirm={() => handleStatusUpdate(item)}
                                  onClose={() => setStatusDialogOpen(false)}
                                />
                              </TableCell>

                              <TableCell>
                                <IconButton
                                  onClick={(e) => handleMenuOpen(e, item)}
                                >
                                  <MoreHoriz />
                                </IconButton>
                              </TableCell>

                              <TableCell
                                align="center"
                                {...provided.dragHandleProps}
                              >
                                <DragIndicator sx={{ color: "#999" }} />
                              </TableCell>
                            </TableRow>
                          )}
                        </Draggable>
                      ))}

                    {provided.placeholder}
                  </TableBody>
                )}
              </Droppable>
            </Table>
          </DragDropContext>

          {loading && (
            <div style={{ padding: 20 }}>
              <CommonShimmer />
            </div>
          )}

          {!loading && data.length === 0 && (
            <div
              style={{
                textAlign: "center",
                padding: "22px 0px",
              }}
            >
              Data not found
            </div>
          )}
        </TableContainer>
      </Paper>

      <CommonMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        items={menuItems}
      />

      <DeletePopup
        open={deleteDialogOpen}
        title="Confirm Delete"
        message="Are you sure you want to delete this question?"
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onClose={() => setDeleteDialogOpen(false)}
      />

      {/* ---------------- Drawer Form ---------------- */}
      <Drawer
        anchor="right"
        open={isCreateNew}
        onClose={() => {
          setIsCreateNew(false);
          dispatch(clearShowMenu());
        }}
        PaperProps={{
          sx: {
            width: "35%",
            borderRadius: "16px 0 0 16px",
            overflow: "hidden",
          },
        }}
      >
        <Box display="flex" flexDirection="column" height="100%">
          <Box
            px={3}
            py={2}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            borderBottom="1px solid #eee"
            bgcolor="#fafafa"
          >
            <Typography variant="h6" fontWeight={600}>
              {formData.Id ? "Edit Question" : "Create Question"}
            </Typography>

            <IconButton
              onClick={() => {
                setIsCreateNew(false);
                dispatch(clearShowMenu());
              }}
            >
              <CancelOutlined />
            </IconButton>
          </Box>

          <Box flex={1} px={3} py={2} overflow="auto">
            <form onSubmit={handleSubmit}>
              {/* Question */}
              <FormControl fullWidth sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight={500} mb={1}>
                  Question
                </Typography>
                <TextField
                  name="question"
                  value={formData.question}
                  onChange={handleChange}
                  placeholder="Enter question"
                  fullWidth
                />
              </FormControl>

              <FormControl fullWidth sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight={500} mb={1}>
                  Category
                </Typography>
                <Select
                  name="categoryId"
                  value={formData.categoryId || ""}
                  onChange={(e) =>
                    setFormData((prev: any) => ({
                      ...prev,
                      categoryId: e.target.value,
                    }))
                  }
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>Select Category</em>
                  </MenuItem>
                  {categories.map((cat: any) => (
                    <MenuItem key={cat._id} value={cat._id}>
                      {cat.category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Question Type */}
              <FormControl fullWidth sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight={500} mb={1}>
                  Question Type
                </Typography>
                <TextField
                  select
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  fullWidth
                >
                  <MenuItem value="radio">Radio</MenuItem>
                  <MenuItem value="text-input">Text Input</MenuItem>
                </TextField>
              </FormControl>

              {/* Radio Options */}
              {formData.type === "radio" && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" fontWeight={500} mb={1}>
                    Options
                  </Typography>

                  {formData.options.map((opt: string, idx: number) => (
                    <Box
                      key={idx}
                      display="flex"
                      alignItems="center"
                      gap={1}
                      mb={1}
                    >
                      <TextField
                        fullWidth
                        placeholder={`Option ${idx + 1}`}
                        value={opt}
                        onChange={(e) =>
                          handleOptionChange(idx, e.target.value)
                        }
                      />
                      <IconButton
                        color="error"
                        onClick={() => removeOption(idx)}
                        disabled={formData.options.length === 1}
                      >
                        <Remove />
                      </IconButton>
                    </Box>
                  ))}

                  <Button
                    sx={{ mt: 1 }}
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={addOption}
                  >
                    + Add Option
                  </Button>
                </Box>
              )}

              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{
                  borderRadius: "10px",
                  py: 1.2,
                  fontWeight: 600,
                  textTransform: "none",
                  backgroundColor: "#000",
                  "&:hover": { backgroundColor: "#222" },
                }}
              >
                Save
              </Button>
            </form>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
}

export default QuickSurveyQuestions;
